import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navigation from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";

// (UI shell — content below)

const EFFECTIVE_DATE = "December 11, 2025";
const COMPANY = "Voxinity AI";
const CONTACT_EMAIL = "support@voxinity.ai";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10" data-testid={`terms-section-${id}`}>
    <h2 className="font-display font-semibold text-2xl md:text-3xl text-white mb-4">
      {title}
    </h2>
    <div className="space-y-4 text-gray-300 leading-relaxed text-base">
      {children}
    </div>
  </section>
);

const TermsPage = () => {
  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="terms-page"
    >
      <Navigation />

      <main className="relative pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors mb-8"
            data-testid="terms-back-link"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>

          <header className="mb-12 border-b border-white/10 pb-8">
            <h1 className="font-display font-semibold text-4xl sm:text-5xl text-white mb-4">
              Terms &amp; Conditions
            </h1>
            <p className="text-gray-400 text-sm">
              Effective Date: <span className="text-gray-200">{EFFECTIVE_DATE}</span>
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Last Updated: <span className="text-gray-200">{EFFECTIVE_DATE}</span>
            </p>
          </header>

          <div className="bg-teal-400/5 border border-teal-400/20 rounded-xl p-5 mb-12">
            <p className="text-sm text-gray-300 leading-relaxed">
              These Terms &amp; Conditions ("Terms") govern your access to and
              use of the {COMPANY} AI voice and chat agent platform (the
              "Service"). By creating an account, installing our application
              from a marketplace, or otherwise using the Service, you agree to
              be bound by these Terms. If you are entering into these Terms on
              behalf of a company or other legal entity, you represent that
              you have the authority to bind that entity.
            </p>
          </div>

          <Section id="acceptance" title="1. Acceptance of Terms">
            <p>
              By accessing or using the Service, you confirm that you are at
              least 18 years old and that you have read, understood, and
              accepted these Terms together with our{" "}
              <Link to="/privacy" className="text-teal-400 hover:underline">
                Privacy Policy
              </Link>
              , which is incorporated herein by reference.
            </p>
          </Section>

          <Section id="description" title="2. Description of the Service">
            <p>
              {COMPANY} provides a software-as-a-service platform that enables
              businesses to deploy AI-powered voice agents, SMS agents, and
              chat widgets that integrate with third-party customer
              relationship management ("CRM") platforms, telephony providers,
              calendar systems, and language model providers. Features may
              include automated calling, conversation memory, function
              calling, appointment scheduling, recording, transcription, and
              analytics.
            </p>
          </Section>

          <Section id="accounts" title="3. Accounts &amp; Eligibility">
            <p>
              You must register for an account to use most features of the
              Service. You agree to (a) provide accurate, current, and
              complete information; (b) maintain the security of your
              credentials; (c) promptly notify us of any unauthorized use;
              and (d) be responsible for all activity that occurs under your
              account. We may suspend or terminate accounts that violate
              these Terms.
            </p>
          </Section>

          <Section id="acceptable-use" title="4. Acceptable Use">
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Make outbound calls or send messages without the prior express
                consent of the recipient where such consent is required by
                law (including TCPA, CAN-SPAM, GDPR, CASL, and similar
                regulations).
              </li>
              <li>
                Place calls to emergency numbers (such as 911), call any
                numbers on a Do-Not-Call registry without an applicable
                exemption, or evade caller-ID requirements.
              </li>
              <li>
                Deploy AI agents that impersonate a real person without
                disclosure, engage in unlawful telemarketing, conduct
                harassment, or transmit threats.
              </li>
              <li>
                Process payment card data, social security numbers, protected
                health information, or other regulated data through the
                Service unless you have a separate written agreement with
                {` ${COMPANY}`} authorizing such processing.
              </li>
              <li>
                Attempt to reverse engineer, decompile, or otherwise extract
                source code from the Service.
              </li>
              <li>
                Use the Service to build a competing product, scrape data,
                circumvent rate limits, or interfere with other users.
              </li>
              <li>
                Upload malware, transmit illegal content, infringe
                intellectual property rights, or violate any applicable law.
              </li>
            </ul>
            <p>
              You are solely responsible for the lawfulness of every call,
              message, and AI interaction initiated through your account,
              including compliance with consumer-protection, telemarketing,
              call-recording-consent, and data-protection laws of every
              jurisdiction involved.
            </p>
          </Section>

          <Section id="ai-disclaimer" title="5. AI Output &amp; Accuracy">
            <p>
              The Service uses large language models and other AI systems to
              generate responses. AI output can be inaccurate, incomplete,
              biased, or otherwise unsuitable for a given purpose. You must
              review and verify AI output before relying on it for any
              decision with legal, financial, medical, or safety
              consequences. {COMPANY} disclaims responsibility for any
              decision made or action taken based on AI-generated content.
            </p>
          </Section>

          <Section id="third-party" title="6. Third-Party Services &amp; Integrations">
            <p>
              The Service interoperates with third-party providers including
              telephony carriers, voice synthesis vendors, language model
              providers, CRM platforms, payment processors, and calendar
              systems. Your use of those third-party services is subject to
              their own terms and privacy policies. {COMPANY} is not
              responsible for, and makes no warranty regarding, any
              third-party service. Where you connect a CRM marketplace
              application, you authorize {COMPANY} to access, read, and
              write data on your behalf within the scopes you approve.
            </p>
          </Section>

          <Section id="customer-data" title="7. Customer Data &amp; Ownership">
            <p>
              As between you and {COMPANY}, you retain all rights, title, and
              interest in and to the data you submit to the Service
              ("Customer Data"). You grant {COMPANY} a worldwide,
              non-exclusive, royalty-free license to host, copy, transmit,
              display, and process Customer Data solely as necessary to (a)
              provide the Service; (b) prevent or address service or
              technical problems; (c) comply with law; and (d) generate
              aggregated, de-identified statistical data that does not
              identify you or any individual.
            </p>
          </Section>

          <Section id="fees" title="8. Fees, Billing &amp; Refunds">
            <p>
              Paid plans are billed in advance on a recurring basis (monthly
              or annually) at the rates posted on our website or otherwise
              agreed in writing. Usage-based charges (e.g., per-minute
              telephony, per-character voice synthesis, per-token AI
              inference) are billed in arrears. All fees are non-refundable
              except where required by law or expressly stated. You authorize
              {` ${COMPANY}`} and our payment processor to charge your payment
              method for all amounts due. Failure to pay may result in
              suspension or termination.
            </p>
          </Section>

          <Section id="ip" title="9. Intellectual Property">
            <p>
              The Service, including all software, designs, text, graphics,
              logos, and other content (excluding Customer Data), is the
              exclusive property of {COMPANY} or its licensors and is
              protected by intellectual property laws. We grant you a
              limited, non-exclusive, non-transferable, revocable license to
              use the Service in accordance with these Terms. All rights not
              expressly granted are reserved.
            </p>
            <p>
              Feedback, suggestions, or ideas you provide are non-confidential
              and may be used by {COMPANY} without restriction or
              compensation.
            </p>
          </Section>

          <Section id="confidentiality" title="10. Confidentiality">
            <p>
              Each party agrees to protect the other's Confidential
              Information with at least the same degree of care it uses for
              its own confidential information, and never less than a
              reasonable degree of care. "Confidential Information" excludes
              information that is public, independently developed, or
              received from a third party without restriction.
            </p>
          </Section>

          <Section id="warranty" title="11. Disclaimers">
            <p className="uppercase tracking-wide text-gray-200 text-sm">
              The Service is provided "as is" and "as available" without
              warranty of any kind. {COMPANY} disclaims all warranties,
              express or implied, including merchantability, fitness for a
              particular purpose, non-infringement, accuracy, and
              uninterrupted operation. {COMPANY} does not warrant that the
              Service will be error-free, that defects will be corrected, or
              that AI output will be accurate or appropriate.
            </p>
          </Section>

          <Section id="liability" title="12. Limitation of Liability">
            <p className="uppercase tracking-wide text-gray-200 text-sm">
              To the maximum extent permitted by law, {COMPANY} and its
              officers, directors, employees, and agents shall not be liable
              for any indirect, incidental, special, consequential, punitive,
              or exemplary damages, including loss of profits, revenue,
              data, goodwill, or business opportunity, arising out of or
              related to the Service. {COMPANY}'s total aggregate liability
              for any claim arising out of or related to these Terms shall
              not exceed the greater of (a) the amounts paid by you to{" "}
              {COMPANY} in the twelve (12) months preceding the event giving
              rise to the claim, or (b) one hundred U.S. dollars (US $100).
            </p>
          </Section>

          <Section id="indemnification" title="13. Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless {COMPANY},
              its affiliates, and their respective officers, directors,
              employees, and agents from and against any claims, liabilities,
              damages, losses, and expenses (including reasonable attorneys'
              fees) arising out of or in any way connected with (a) your use
              of the Service; (b) Customer Data; (c) your violation of these
              Terms or any applicable law; or (d) your violation of any
              third-party right, including any consumer-protection,
              telemarketing, or data-protection law.
            </p>
          </Section>

          <Section id="termination" title="14. Termination">
            <p>
              You may terminate your account at any time from the Service
              billing settings. We may suspend or terminate your access
              immediately if you breach these Terms, fail to pay fees, or
              if we are required to do so by law. Upon termination, your
              right to use the Service ends, and we may delete Customer Data
              after a reasonable retention window. Sections that by their
              nature should survive (including 7, 9, 11, 12, 13, 15, and
              16) will survive termination.
            </p>
          </Section>

          <Section id="governing-law" title="15. Governing Law &amp; Dispute Resolution">
            <p>
              These Terms are governed by the laws of the State of Delaware,
              United States of America, without regard to conflict-of-laws
              principles. The parties consent to the exclusive jurisdiction of
              the state and federal courts located in Delaware for any dispute
              that is not subject to arbitration.
            </p>
            <p>
              <span className="text-white font-medium">Informal Resolution:</span>{" "}
              Before filing any claim, the parties agree to attempt good-faith
              negotiation for at least 30 days after written notice.
            </p>
            <p>
              <span className="text-white font-medium">Class Action Waiver:</span>{" "}
              Disputes must be brought in an individual capacity and not as a
              plaintiff or class member in any purported class or
              representative proceeding, to the extent permitted by law.
            </p>
          </Section>

          <Section id="changes" title="16. Changes to the Terms">
            <p>
              We may modify these Terms from time to time. Material changes
              will be communicated by email or via an in-product notice at
              least 30 days before they take effect. Your continued use of
              the Service after the effective date constitutes acceptance of
              the modified Terms.
            </p>
          </Section>

          <Section id="general" title="17. General Provisions">
            <p>
              <span className="text-white font-medium">Entire Agreement.</span>{" "}
              These Terms, together with the Privacy Policy and any order
              forms, constitute the entire agreement between you and {COMPANY}.
            </p>
            <p>
              <span className="text-white font-medium">Severability.</span> If
              any provision is held unenforceable, the remaining provisions
              remain in full force and effect.
            </p>
            <p>
              <span className="text-white font-medium">No Waiver.</span> Our
              failure to enforce any right or provision will not constitute a
              waiver.
            </p>
            <p>
              <span className="text-white font-medium">Assignment.</span> You
              may not assign these Terms without our prior written consent.
              We may assign these Terms in connection with a merger,
              acquisition, or sale of assets.
            </p>
            <p>
              <span className="text-white font-medium">Force Majeure.</span>{" "}
              Neither party will be liable for delays or failures due to
              circumstances beyond reasonable control.
            </p>
          </Section>

          <Section id="contact" title="18. Contact Us">
            <p>
              Questions about these Terms should be sent to{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-teal-400 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <div className="mt-16 p-5 rounded-xl border border-amber-400/20 bg-amber-400/5 text-sm text-amber-100/80">
            <p className="font-medium text-amber-200 mb-1">
              Legal Disclaimer
            </p>
            <p>
              This document is provided for general informational purposes
              and does not constitute legal advice. {COMPANY} recommends that
              you consult qualified legal counsel to confirm that these Terms
              meet the regulatory requirements applicable to your business
              and the jurisdictions in which you operate.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
